import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { ButtonRing } from "../components/LoadingRing";

const inputCls =
    "w-full bg-bg1 border border-white/10 text-white rounded-none px-4 py-3 focus:border-gold outline-none transition-colors placeholder:text-zinc-600";

export default function AccountAuth({ mode = "login" }) {
    const { customer, login, register, loginWithGoogle } = useCustomerAuth();
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();
    const isRegister = mode === "register";

    if (customer) return <Navigate to="/account" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = isRegister
            ? await register(form)
            : await login(form.email.trim(), form.password);
        setBusy(false);
        if (res.ok) {
            toast.success(isRegister ? "Account created" : "Welcome back");
            nav("/account");
        } else {
            toast.error(res.error);
        }
    };

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid={`account-${mode}-page`}>
            <div className="mx-auto max-w-md px-6">
                <div className="eyebrow mb-4">{isRegister ? "Create Account" : "Member Sign In"}</div>
                <h1 className="font-display text-4xl md:text-5xl tracking-tight">
                    {isRegister ? "Join ReHealthMe" : "Welcome Back"}
                </h1>
                <p className="text-sm text-zinc-400 mt-3">
                    {isRegister
                        ? "Create an account to unlock your 50% first-order offer, track orders and get reorder reminders."
                        : "Sign in to track orders, reorder faster and manage your details."}
                </p>

                <button
                    onClick={loginWithGoogle}
                    data-testid="google-signin-button"
                    className="mt-8 w-full btn-pill btn-secondary !py-3.5"
                >
                    CONTINUE WITH GOOGLE
                </button>

                <div className="flex items-center gap-4 my-7">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="mono text-[9px] tracking-[0.2em] text-zinc-600">OR WITH EMAIL</span>
                    <div className="flex-1 h-px bg-white/10" />
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid={`account-${mode}-form`}>
                    {isRegister && (
                        <input className={inputCls} placeholder="Full name" required value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" />
                    )}
                    <input className={inputCls} type="email" placeholder="Email address" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email" />
                    {isRegister && (
                        <input className={inputCls} placeholder="Phone (optional)" value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="account-phone" />
                    )}
                    <input className={inputCls} type="password" placeholder={isRegister ? "Password (min 8 characters)" : "Password"}
                        required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="account-password" />

                    <button type="submit" disabled={busy} className="btn-pill btn-primary w-full !py-3.5"
                        data-testid={`account-${mode}-submit`}>
                        {busy ? <ButtonRing label="PLEASE WAIT…" /> : isRegister ? "CREATE ACCOUNT" : "SIGN IN"}
                    </button>
                </form>

                <div className="mt-6 text-sm text-zinc-500">
                    {isRegister ? (
                        <>Already have an account? <Link to="/account/login" className="text-gold" data-testid="switch-to-login">Sign in</Link></>
                    ) : (
                        <>New to ReHealthMe? <Link to="/account/register" className="text-gold" data-testid="switch-to-register">Create an account</Link></>
                    )}
                </div>
            </div>
        </div>
    );
}
